
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Key, Save, ExternalLink, RefreshCw, Edit, Check } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const apiKeySchema = z.object({
  youtubeApiKey: z.string().min(1, 'La clé API est obligatoire').refine(val => /^AIza[0-9A-Za-z_-]{35}$/.test(val), {
    message: 'Format de clé API YouTube invalide. La clé doit commencer par "AIza" suivi de 35 caractères.'
  })
});

type ApiKeyFormValues = z.infer<typeof apiKeySchema>;

const ApiKeyForm = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');

  const form = useForm<ApiKeyFormValues>({
    resolver: zodResolver(apiKeySchema),
    defaultValues: {
      youtubeApiKey: ''
    }
  });

  // Charger la clé API de l'utilisateur
  useEffect(() => {
    const fetchApiKey = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('youtube_api_key')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data?.youtube_api_key) {
          // Stocker la clé complète dans le formulaire
          form.setValue('youtubeApiKey', data.youtube_api_key);
          
          // Créer une version masquée pour l'affichage
          const key = data.youtube_api_key;
          const masked = key.substring(0, 6) + '•'.repeat(key.length - 10) + key.substring(key.length - 4);
          setMaskedKey(masked);
          
          setHasExistingKey(true);
        } else {
          setHasExistingKey(false);
          setIsEditing(true); // Mode édition par défaut s'il n'y a pas de clé
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la clé API:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchApiKey();
  }, [user, form]);

  const onSubmit = async (values: ApiKeyFormValues) => {
    if (!user) {
      toast({
        title: "Non connecté",
        description: "Vous devez être connecté pour enregistrer une clé API.",
        variant: "destructive"
      });
      return;
    }
    
    // Tester la clé API avant de l'enregistrer
    setTestStatus('testing');
    setTestMessage('Vérification de la validité de la clé API...');
    
    try {
      // Faire un appel léger à l'API YouTube pour tester la validité de la clé
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=id&id=UC_x5XG1OV2P6uZZ5FSM9Ttw&key=${values.youtubeApiKey}`
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 400 && 
            data.error && 
            data.error.errors && 
            data.error.errors.some((e: any) => e.reason === 'keyInvalid')) {
          setTestStatus('error');
          setTestMessage('Clé API invalide. Veuillez vérifier que vous avez correctement copié votre clé.');
          return;
        } else {
          setTestStatus('error');
          setTestMessage(`Erreur API: ${data.error?.message || 'Erreur inconnue'}`);
          return;
        }
      }
      
      // Si on arrive ici, la clé est valide
      setTestStatus('success');
      setTestMessage('Clé API valide ! Enregistrement en cours...');
      
      // Maintenant on peut l'enregistrer
      setLoading(true);
      const { error } = await supabase
        .from('profiles')
        .update({ youtube_api_key: values.youtubeApiKey })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: hasExistingKey ? "Clé API mise à jour" : "Clé API enregistrée",
        description: hasExistingKey 
          ? "Votre clé API YouTube a été mise à jour avec succès." 
          : "Votre clé API YouTube a été enregistrée avec succès.",
        variant: "default"
      });
      
      // Mettre à jour l'état local
      setHasExistingKey(true);
      setIsEditing(false);
      
      // Masquer la nouvelle clé
      const key = values.youtubeApiKey;
      const masked = key.substring(0, 6) + '•'.repeat(key.length - 10) + key.substring(key.length - 4);
      setMaskedKey(masked);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'enregistrement de la clé API:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de l'enregistrement de la clé API.",
        variant: "destructive"
      });
      
      setTestStatus('error');
      setTestMessage('Une erreur est survenue lors de l\'enregistrement de la clé API.');
    } finally {
      setLoading(false);
    }
  };
  
  const testApiKey = async () => {
    const apiKey = form.getValues().youtubeApiKey;
    
    if (!apiKey) {
      setTestStatus('error');
      setTestMessage('Veuillez d\'abord saisir une clé API.');
      return;
    }
    
    if (form.formState.errors.youtubeApiKey) {
      setTestStatus('error');
      setTestMessage('Le format de la clé API est invalide. Corrigez-le avant de tester.');
      return;
    }
    
    setTestStatus('testing');
    setTestMessage('Test de la clé API en cours...');
    
    try {
      // Faire un appel léger à l'API YouTube pour tester la validité de la clé
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=id&id=UC_x5XG1OV2P6uZZ5FSM9Ttw&key=${apiKey}`
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setTestStatus('success');
        setTestMessage('Clé API valide et fonctionnelle !');
      } else if (response.status === 403 && 
                 data.error && 
                 data.error.errors && 
                 data.error.errors.some((e: any) => e.reason === 'quotaExceeded')) {
        setTestStatus('error');
        setTestMessage('La clé API est valide mais a dépassé son quota quotidien.');
      } else if (response.status === 400 && 
                 data.error && 
                 data.error.errors && 
                 data.error.errors.some((e: any) => e.reason === 'keyInvalid')) {
        setTestStatus('error');
        setTestMessage('Clé API invalide. Veuillez vérifier que vous avez correctement copié votre clé.');
      } else {
        setTestStatus('error');
        setTestMessage(`Erreur API: ${data.error?.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur lors du test de la clé API:', error);
      setTestStatus('error');
      setTestMessage('Une erreur est survenue lors du test de la clé API.');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTestStatus('idle');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="w-5 h-5 text-youtube-red" />
          Clé API YouTube
        </CardTitle>
        <CardDescription>
          {hasExistingKey 
            ? "Votre clé API YouTube est déjà configurée. Vous pouvez la modifier ci-dessous."
            : "Ajoutez votre propre clé API YouTube pour accéder aux fonctionnalités de l'application."}
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {hasExistingKey && !isEditing ? (
              <div className="space-y-2">
                <FormLabel>Clé API YouTube actuelle</FormLabel>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-md px-3 py-2 font-mono text-sm overflow-hidden text-ellipsis whitespace-nowrap border border-gray-200 dark:border-gray-700">
                    {maskedKey}
                  </div>
                  <Button 
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEdit}
                    disabled={loading}
                    className="whitespace-nowrap"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                </div>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="youtubeApiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {hasExistingKey ? "Nouvelle clé API YouTube" : "Clé API YouTube"} 
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="AIzaSyXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormDescription className="flex items-center gap-1">
                      <span>Votre clé API YouTube personnelle</span>
                      <a 
                        href="https://console.cloud.google.com/apis/credentials" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 inline-flex items-center gap-1"
                      >
                        <span>Obtenir une clé</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {isEditing && testStatus !== 'idle' && (
              <div className={`p-3 rounded-md text-sm ${
                testStatus === 'success' 
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                  : testStatus === 'error'
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    : 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
              }`}>
                {testMessage}
              </div>
            )}
            
            {isEditing && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={testApiKey}
                  disabled={loading || testStatus === 'testing'}
                  className="text-xs"
                >
                  {testStatus === 'testing' ? (
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Tester la clé
                </Button>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-wrap gap-2">
            {isEditing && hasExistingKey && (
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="flex-1 sm:flex-none"
              >
                Annuler
              </Button>
            )}
            {isEditing && (
              <Button 
                type="submit" 
                disabled={loading || testStatus === 'testing' || !form.formState.isValid}
                className="flex-1 sm:flex-none"
              >
                {loading || testStatus === 'testing' ? (
                  <>
                    <span className="animate-spin mr-2">○</span>
                    {testStatus === 'testing' ? 'Vérification...' : 'Enregistrement...'}
                  </>
                ) : (
                  <>
                    {hasExistingKey ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Mettre à jour la clé
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer la clé
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default ApiKeyForm;
